// App-wide alert / confirm / prompt dialogs. Every page uses these instead
// of window.alert/confirm/prompt or ad-hoc inline confirmations, so each
// kind of message looks and behaves the same everywhere:
//
//   const { confirm } = useDialogs()
//   if (await confirm({ kind: 'delete', title: 'Disconnect Claude CLI?' })) …
//
// Each kind has its own goblin (with illustrations on) and tint. Dangerous
// kinds (delete, warning) focus Cancel by default; Escape always cancels.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react'
import { useIllustrations } from '../lib/illustrations'

export type DialogKind =
  'info' | 'success' | 'warning' | 'error' | 'question' | 'delete'

interface BaseOptions {
  kind?: DialogKind
  title: string
  message?: ReactNode
  /** A different goblin than the kind's own (a /goblins/*.webp path). */
  art?: string
}

export interface AlertOptions extends BaseOptions {
  confirmLabel?: string
}

export interface ConfirmOptions extends BaseOptions {
  confirmLabel?: string
  cancelLabel?: string
}

export interface PromptOptions extends ConfirmOptions {
  label: string
  placeholder?: string
  defaultValue?: string
  /** Confirm stays disabled until something is typed (default true). */
  required?: boolean
  multiline?: boolean
}

export interface DialogApi {
  alert: (options: AlertOptions) => Promise<void>
  confirm: (options: ConfirmOptions) => Promise<boolean>
  prompt: (options: PromptOptions) => Promise<string | null>
}

type Request =
  | { type: 'alert'; options: AlertOptions; resolve: (value: void) => void }
  | {
      type: 'confirm'
      options: ConfirmOptions
      resolve: (value: boolean) => void
    }
  | {
      type: 'prompt'
      options: PromptOptions
      resolve: (value: string | null) => void
    }

const DialogContext = createContext<DialogApi | null>(null)

/** Tint behind the illustration, per kind. */
const TINT: Record<DialogKind, string> = {
  info: 'bg-info-soft',
  success: 'bg-ok-soft',
  warning: 'bg-signal-soft',
  error: 'bg-danger-soft',
  question: 'bg-moss-soft',
  delete: 'bg-danger-soft',
}

/** Small marker shown instead of the goblin when illustrations are off. */
const DOT: Record<DialogKind, string> = {
  info: 'bg-info',
  success: 'bg-ok',
  warning: 'bg-signal',
  error: 'bg-danger',
  question: 'bg-moss',
  delete: 'bg-danger',
}

const DEFAULT_KIND: Record<Request['type'], DialogKind> = {
  alert: 'info',
  confirm: 'question',
  prompt: 'question',
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Request[]>([])

  const push = useCallback((request: Request) => {
    setQueue((current) => [...current, request])
  }, [])

  const api = useMemo<DialogApi>(
    () => ({
      alert: (options) =>
        new Promise((resolve) => push({ type: 'alert', options, resolve })),
      confirm: (options) =>
        new Promise((resolve) => push({ type: 'confirm', options, resolve })),
      prompt: (options) =>
        new Promise((resolve) => push({ type: 'prompt', options, resolve })),
    }),
    [push]
  )

  const current = queue[0]
  const close = useCallback(() => setQueue((q) => q.slice(1)), [])

  return (
    <DialogContext.Provider value={api}>
      {children}
      {current ? (
        <DialogView
          key={queue.length + current.options.title}
          request={current}
          onClose={close}
        />
      ) : null}
    </DialogContext.Provider>
  )
}

export function useDialogs(): DialogApi {
  const api = useContext(DialogContext)
  if (!api) throw new Error('useDialogs must be used inside <DialogProvider>')
  return api
}

function DialogView({
  request,
  onClose,
}: {
  request: Request
  onClose: () => void
}) {
  const [illustrations] = useIllustrations()
  const titleId = useId()
  const messageId = useId()
  const inputId = useId()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const confirmRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null)

  const { options } = request
  const kind = options.kind ?? DEFAULT_KIND[request.type]
  const promptOptions = request.type === 'prompt' ? request.options : null
  const [value, setValue] = useState(promptOptions?.defaultValue ?? '')
  const hasCancel = request.type !== 'alert'
  const destructive = kind === 'delete' || kind === 'error'
  const required = promptOptions ? promptOptions.required !== false : false

  function cancel(): void {
    if (request.type === 'alert') request.resolve()
    else if (request.type === 'confirm') request.resolve(false)
    else request.resolve(null)
    onClose()
  }

  function accept(): void {
    if (request.type === 'alert') request.resolve()
    else if (request.type === 'confirm') request.resolve(true)
    else {
      if (required && !value.trim()) return
      request.resolve(value.trim())
    }
    onClose()
  }

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    // Safe default focus: the input for a prompt, Cancel for anything that
    // destroys or warns, otherwise the main action.
    if (promptOptions) inputRef.current?.focus()
    else if (hasCancel && (kind === 'delete' || kind === 'warning'))
      cancelRef.current?.focus()
    else confirmRef.current?.focus()
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault()
        cancel()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      previouslyFocused?.focus?.()
    }
    // Focus once per dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    accept()
  }

  const confirmLabel =
    options.confirmLabel ??
    (request.type === 'alert'
      ? 'OK'
      : kind === 'delete'
        ? 'Delete'
        : 'Continue')
  const cancelLabel = (options as ConfirmOptions).cancelLabel ?? 'Cancel'

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-ink/40 motion-safe:animate-[fade_120ms_ease-out]"
        onClick={hasCancel ? cancel : undefined}
        aria-hidden
      />
      <form
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={options.message ? messageId : undefined}
        onSubmit={handleSubmit}
        className="relative w-full max-w-md overflow-hidden rounded-[16px] border border-line bg-surface shadow-overlay motion-safe:animate-[fade_140ms_ease-out]"
        data-kind={kind}
      >
        {illustrations ? (
          <div className={`flex justify-center pb-1 pt-5 ${TINT[kind]}`}>
            <img
              src={options.art ?? `/goblins/dialog-${kind}.webp`}
              alt=""
              aria-hidden
              width={112}
              height={112}
              className="h-28 w-28 select-none object-contain"
            />
          </div>
        ) : (
          <div className={`h-1.5 ${DOT[kind]}`} aria-hidden />
        )}

        <div className="px-6 pb-5 pt-4">
          <h2
            id={titleId}
            className="font-display text-lg font-semibold text-ink"
          >
            {options.title}
          </h2>
          {options.message ? (
            <div id={messageId} className="mt-1.5 text-base text-ink-2">
              {options.message}
            </div>
          ) : null}

          {promptOptions ? (
            <div className="mt-4">
              <label
                htmlFor={inputId}
                className="mb-1.5 block text-base font-medium text-ink"
              >
                {promptOptions.label}
              </label>
              {promptOptions.multiline ? (
                <textarea
                  id={inputId}
                  ref={inputRef}
                  className="field-input"
                  rows={3}
                  placeholder={promptOptions.placeholder}
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                />
              ) : (
                <input
                  id={inputId}
                  ref={inputRef}
                  className="field-input"
                  placeholder={promptOptions.placeholder}
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                />
              )}
            </div>
          ) : null}

          <div className="mt-5 flex justify-end gap-2">
            {hasCancel ? (
              <button
                type="button"
                ref={cancelRef}
                className="btn-ghost"
                onClick={cancel}
              >
                {cancelLabel}
              </button>
            ) : null}
            <button
              type="submit"
              ref={confirmRef}
              className={
                destructive && hasCancel ? 'btn-danger' : 'btn-primary'
              }
              disabled={required && !value.trim()}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
